import { SIGN_OUT } from 'store/auth/actions';
import { createReducer } from 'typesafe-actions';
import {
  defaultCssPropertyAnalysisFilter,
  defaultCssPropertyAnalysisFilterOptions,
  defaultCssPropertyAnalysisPagination,
  defaultCssPropertyAnalysisSummary,
  PropertyAnalysisState,
} from 'types/PropertyAnalysis';
import {
  doPropertyAnalysisClear,
  doPropertyAnalysisDatasetDecisionUpdate,
  doPropertyAnalysisDatasetDecisionUpdateFailure,
  doPropertyAnalysisDatasetDecisionUpdateSuccess,
  doPropertyAnalysisDerivedFieldUpdate,
  doPropertyAnalysisDerivedFieldUpdateFailure,
  doPropertyAnalysisDerivedFieldUpdateSuccess,
  doPropertyAnalysisExport,
  doPropertyAnalysisExportFailure,
  doPropertyAnalysisExportSuccess,
  doPropertyAnalysisFilterOptionsLoad,
  doPropertyAnalysisFilterOptionsLoadFailure,
  doPropertyAnalysisFilterOptionsLoadSuccess,
  doPropertyAnalysisLoad,
  doPropertyAnalysisLoadFailure,
  doPropertyAnalysisLoadSuccess,
} from './actions';

const splitValues = (value: string | null) =>
  value
    ?.split(';')
    .map((item) => item.trim().toLowerCase())
    .filter(Boolean) ?? [];

const matchesSelectedValues = (
  value: string | null,
  selectedValues: string[],
) =>
  !selectedValues.length ||
  selectedValues.some((selectedValue) =>
    splitValues(value).includes(selectedValue.trim().toLowerCase()),
  );

const hasConflicts = (item: PropertyAnalysisState['items'][number]) => {
  const reviewRequired = item.derived.reviewRequired?.trim().toLowerCase();
  return (
    reviewRequired === 'yes' ||
    (reviewRequired !== 'no' &&
      [
        item.conflicts.conflictSummary,
        item.conflicts.heightConflictDetails,
        item.conflicts.externalWallConflictDetails,
        item.conflicts.ageConflictDetails,
      ].some((value) => Boolean(value?.trim())))
  );
};

const matchesFilter = (
  item: PropertyAnalysisState['items'][number],
  filter: PropertyAnalysisState['filter'],
) =>
  matchesSelectedValues(item.derived.screeningPriority, filter.priorities) &&
  matchesSelectedValues(
    item.derived.datasetDecision,
    filter.datasetDecisions,
  ) &&
  matchesSelectedValues(item.derived.heightCategory, filter.heightCategories) &&
  matchesSelectedValues(
    item.derived.buildingAgeBand,
    filter.buildingAgeBands,
  ) &&
  matchesSelectedValues(
    item.derived.populationDensityBand,
    filter.populationDensityBands,
  ) &&
  (filter.hasConflicts === undefined ||
    hasConflicts(item) === filter.hasConflicts);

const decisionSummaryKey = (decision: string | null) => {
  switch (decision?.trim().toLowerCase()) {
    case 'pending':
      return 'pending';
    case 'include':
      return 'included';
    case 'exclude':
      return 'excluded';
    default:
      return null;
  }
};

const replaceUpdatedItem = (
  state: PropertyAnalysisState,
  updatedItem: PropertyAnalysisState['items'][number],
): PropertyAnalysisState => {
  const previousItem = state.items.find(({ id }) => id === updatedItem.id);
  if (!previousItem) {
    return state;
  }

  const previousMatchesFilter = matchesFilter(previousItem, state.filter);
  const updatedMatchesFilter = matchesFilter(updatedItem, state.filter);
  const summary = { ...state.summary };
  const previousDecision = decisionSummaryKey(
    previousItem.derived.datasetDecision,
  );
  const updatedDecision = decisionSummaryKey(
    updatedItem.derived.datasetDecision,
  );

  if (previousMatchesFilter && previousDecision !== updatedDecision) {
    if (previousDecision) {
      summary[previousDecision] = Math.max(0, summary[previousDecision] - 1);
    }
    if (updatedMatchesFilter && updatedDecision) {
      summary[updatedDecision] += 1;
    } else {
      summary.total = Math.max(0, summary.total - 1);
    }
  }

  return {
    ...state,
    items: state.items.map((item) =>
      item.id === updatedItem.id ? updatedItem : item,
    ),
    summary,
  };
};

export const defaultPropertyAnalysisState: PropertyAnalysisState = {
  items: [],
  updatedProperty: null,
  summary: defaultCssPropertyAnalysisSummary,
  pagination: defaultCssPropertyAnalysisPagination,
  filter: defaultCssPropertyAnalysisFilter,
  filterOptions: defaultCssPropertyAnalysisFilterOptions,
  filterOptionsLoadStatus: null,
  isLoading: false,
  isLoaded: false,
  error: null,
  exportLoadStatus: null,
  exportError: null,
  decisionUpdatePropertyId: null,
  decisionUpdateError: null,
  decisionUpdateErrorPropertyId: null,
  derivedFieldUpdatePropertyId: null,
  derivedFieldUpdateField: null,
  derivedFieldUpdateError: null,
};

export const propertyAnalysisReducer = createReducer(
  defaultPropertyAnalysisState,
)
  .handleAction(SIGN_OUT.success, () => defaultPropertyAnalysisState)
  .handleAction(doPropertyAnalysisClear, () => defaultPropertyAnalysisState)
  .handleAction(doPropertyAnalysisFilterOptionsLoad, (state) => ({
    ...state,
    filterOptionsLoadStatus: 'loading',
  }))
  .handleAction(
    doPropertyAnalysisFilterOptionsLoadSuccess,
    (state, action) => ({
      ...state,
      filterOptions: action.payload.filterOptions,
      filterOptionsLoadStatus: 'loaded',
    }),
  )
  .handleAction(doPropertyAnalysisFilterOptionsLoadFailure, (state) => ({
    ...state,
    filterOptionsLoadStatus: 'error',
  }))
  .handleAction(doPropertyAnalysisLoad, (state, action) => {
    const page = action.payload.page ?? 1;
    return {
      ...state,
      filter: { ...state.filter, ...action.payload.filter },
      isLoading: true,
      isLoaded: page > 1 ? state.isLoaded : false,
      error: null,
      decisionUpdateError: null,
      decisionUpdateErrorPropertyId: null,
    };
  })
  .handleAction(doPropertyAnalysisExport, (state) => ({
    ...state,
    exportLoadStatus: 'loading',
    exportError: null,
  }))
  .handleAction(doPropertyAnalysisExportSuccess, (state) => ({
    ...state,
    exportLoadStatus: 'loaded',
    exportError: null,
  }))
  .handleAction(doPropertyAnalysisExportFailure, (state, action) => ({
    ...state,
    exportLoadStatus: 'error',
    exportError: action.payload.error,
  }))
  .handleAction(doPropertyAnalysisDatasetDecisionUpdate, (state, action) => ({
    ...state,
    decisionUpdatePropertyId: action.payload.propertyId,
    decisionUpdateError: null,
    decisionUpdateErrorPropertyId: null,
  }))
  .handleAction(
    doPropertyAnalysisDatasetDecisionUpdateSuccess,
    (state, action) => ({
      ...replaceUpdatedItem(state, action.payload.property),
      updatedProperty: action.payload.property,
      decisionUpdatePropertyId: null,
      decisionUpdateError: null,
      decisionUpdateErrorPropertyId: null,
    }),
  )
  .handleAction(
    doPropertyAnalysisDatasetDecisionUpdateFailure,
    (state, action) => ({
      ...state,
      decisionUpdatePropertyId: null,
      decisionUpdateError: action.payload.error,
      decisionUpdateErrorPropertyId: action.payload.propertyId,
    }),
  )
  .handleAction(doPropertyAnalysisDerivedFieldUpdate, (state, action) => ({
    ...state,
    derivedFieldUpdatePropertyId: action.payload.propertyId,
    derivedFieldUpdateField: action.payload.field,
    derivedFieldUpdateError: null,
  }))
  .handleAction(
    doPropertyAnalysisDerivedFieldUpdateSuccess,
    (state, action) => ({
      ...replaceUpdatedItem(state, action.payload.property),
      updatedProperty: action.payload.property,
      derivedFieldUpdatePropertyId: null,
      derivedFieldUpdateField: null,
      derivedFieldUpdateError: null,
    }),
  )
  .handleAction(
    doPropertyAnalysisDerivedFieldUpdateFailure,
    (state, action) => ({
      ...state,
      derivedFieldUpdatePropertyId: null,
      derivedFieldUpdateField: null,
      derivedFieldUpdateError: action.payload.error,
    }),
  )
  .handleAction(doPropertyAnalysisLoadSuccess, (state, action) => ({
    ...state,
    items: action.payload.append
      ? [
          ...state.items.filter(
            (item) =>
              !action.payload.items.some(
                (updatedItem) => updatedItem.id === item.id,
              ),
          ),
          ...action.payload.items,
        ]
      : action.payload.items,
    summary: action.payload.summary,
    pagination: action.payload.pagination,
    isLoading: false,
    isLoaded: true,
    error: null,
  }))
  .handleAction(doPropertyAnalysisLoadFailure, (state, action) => ({
    ...state,
    isLoading: false,
    isLoaded: action.payload.page > 1 ? state.isLoaded : false,
    error: action.payload.error,
  }));
